import {
  APPLICATION_TYPE,
  DOC_UPLOAD_STATUS_IDS,
  useApplication,
  useFileUpload,
  useGoogleAnalytics,
  useModals,
  useDocumentUploads,
  authenticateDocument,
  isAidUploadDocumentDesktopModeAllowed,
} from "@ca-dmv-radv/data";
import {
  CAPTURING_PHOTOS_IDLE,
  PROCESSING_PHOTOS_ERROR,
  PROCESSING_PHOTOS_IDLE,
  PROCESSING_PHOTOS_PROCESSING,
  PROCESSING_PHOTOS_SUCCESS,
  PROCESSING_PHOTOS_UPLOADING,
  useABBYYCapture,
} from "@ca-dmv-radv/files";
import { Trans, useTranslation } from "@ca-dmv-radv/translation";
import { ABBYY_ERROR_STOP, DocumentUpload } from "@ca-dmv/core";
import { React, useCallback, useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { useMounted } from "@ca-dmv-radv/utilities";
import { initializeAID } from "@ca-dmv-radv/files/src/useAIDCapture";
import AIDDocumentCapture from "../AIDDocumentCapture";
import {
  aidCaptureError,
  aidEnabledCatDocIds,
  isAbbyyFlow,
  isMDLMobile,
  isDocument,
  Documents,
} from "../helpers/constants";
import useTranslateDocumentName from "../helpers/useTranslateDocumentName";
import RequiredPhotos from "../RequiredPhotos";
import Upload from "../Upload";
import ErrorExplanation from "./ErrorExplanation";
import errorStatus from "./errorStatus";
import MobileVerificationModal from "./MobileVerificationModal";
import ProcessingPhotosErrorModal from "./ProcessingPhotosErrorModal";
import ProcessingPhotosModal from "./ProcessingPhotosModal";
import ProcessingPhotosSuccessModal from "./ProcessingPhotosSuccessModal";
import SelectDifferentDocument from "./SelectDifferentDocument";
import TakePhotoButton from "./TakePhotoButton";
import {
  isFailureStatus,
  isPendingStatus,
  isRetryStatus,
  isSuccessStatus,
  isPendingConfirmationStatus,
  isAIDCapture,
  generateUrl,
} from "../PresenceVerification/constants";

// TODO: Make transactionId a const
// eslint-disable-next-line import/no-mutable-exports
let transactionId;
let isLoadedOnce;

// Category IDs whose documents go through AID/ABBYY identity capture
// (birth certificate, passport, passport card flows)
const IDENTITY_DOCUMENT_CATEGORY_IDS = [1, 7, 8];

// MTF/MTS document category
const MTF_MTS_CATEGORY_ID = 6;

// "Select a different document" is always hidden for this category, regardless of status
const CATEGORY_ID_HIDDEN_FROM_SELECT_DIFFERENT = 7;

const isIdentityCaptureCategory = (categoryId) =>
  IDENTITY_DOCUMENT_CATEGORY_IDS.includes(categoryId);

export default function Document({
  containerClass,
  documentUpload,
  onCancelDocument,
  onSelectDifferentDocument,
  selectDifferentDocumentLabel,
  confirmFields,
  setConfirmFields,
  showIdentityForm,
  setShowIdentityForm,
}) {
  if (!documentUpload) {
    return null;
  }
  const { t } = useTranslation();
  const {
    uploadingDocumentId,
    setUploadingDocumentId,
    takingPhotoDocumentId,
    setTakingPhotoDocumentId,
    selectDifferentDocumentId,
    setSelectDifferentDocumentId,
  } = useModals(null);
  const { fetchDocumentUploads, setShouldPingDocumentUploads } =
    useDocumentUploads();
  const { setFiles, postUploads } = useFileUpload();
  const { fetchApplication, applicationType, documentTypes } = useApplication();
  const { setAnalyticsAction } = useGoogleAnalytics();
  const translatedDocumentName = useTranslateDocumentName(
    documentUpload,
    applicationType
  );

  const { application } = useApplication();
  const { documentUploads } = useDocumentUploads();

  const uploadDocumentFiles = useCallback(
    async (doc) => {
      setShouldPingDocumentUploads(false);
      setAnalyticsAction(
        `Uploading ${doc.category}${doc.doc ? ` ${doc.doc}` : ""}`
      );
      const uploadSuccess = await postUploads(doc);
      await Promise.all([fetchApplication(), fetchDocumentUploads()]);
      setShouldPingDocumentUploads(true);
      setFiles((currentFiles) => {
        const newFiles = { ...currentFiles };
        delete newFiles[doc.id];
        return newFiles;
      });
      return uploadSuccess;
    },
    [
      setShouldPingDocumentUploads,
      setAnalyticsAction,
      postUploads,
      fetchApplication,
      fetchDocumentUploads,
      setFiles,
    ]
  );

  const {
    requiredPhotos,
    setRequiredPhotos,
    processingPhotos,
    setProcessingPhotos,
    verificationRequired,
    setVerificationRequired,
    resetUseABBYState,
    setIsCapturing,
    mutliPageError,
    setMultipageError,
    aidCaptureErrorCode,
    setAidCaptureErrorCode,
  } = useABBYYCapture({
    chosenDocument: documentUpload,
    uploadDocumentFiles,
  });
  const { setPassportFormData, passportFormData, resetFormData } =
    useFileUpload();
  const documentErrorStatus = errorStatus({ documentUpload });

  // Statuses where the document is either finalized or actively being reviewed/processed.
  // Used to hide "select a different document" / "change image" and to show the active
  const inProgressOrFinalStatuses = [
    DOC_UPLOAD_STATUS_IDS.VERIFIED,
    DOC_UPLOAD_STATUS_IDS.VERIFYING,
    DOC_UPLOAD_STATUS_IDS.UNDER_REVIEW,
    DOC_UPLOAD_STATUS_IDS.FAILED,
    DOC_UPLOAD_STATUS_IDS.UPLOADING,
    DOC_UPLOAD_STATUS_IDS.PROCESSING,
  ];

  const activeCheckStatuses = [
    DOC_UPLOAD_STATUS_IDS.VERIFYING,
    DOC_UPLOAD_STATUS_IDS.UNDER_REVIEW,
    DOC_UPLOAD_STATUS_IDS.FAILED,
    DOC_UPLOAD_STATUS_IDS.PROCESSING,
    DOC_UPLOAD_STATUS_IDS.UPLOADING,
  ];

  const successRedirectApplicationTypes = [
    APPLICATION_TYPE.VDL,
    APPLICATION_TYPE.RVD,
    APPLICATION_TYPE.RVO,
    APPLICATION_TYPE.RPC,
  ];

  const isStatusEligibleForDocumentSwap = !inProgressOrFinalStatuses.includes(
    documentUpload.statusId
  );
  const isUnresolvedAbbyyError = documentErrorStatus === ABBYY_ERROR_STOP;
  const isPucAwaitingConfirmation =
    documentUpload.ATD === "PUC" && !confirmFields;

  const showSelectDifferentDocument =
    (isStatusEligibleForDocumentSwap ||
      isUnresolvedAbbyyError ||
      isPucAwaitingConfirmation) &&
    documentUpload.categoryId !== CATEGORY_ID_HIDDEN_FROM_SELECT_DIFFERENT;

  const [takePhotoClicked, setTakePhotoClicked] = useState(false);
  const [extractedDetails, setExtractedDetails] = useState({});
  const [extractInProgress, setExtractInProgress] = useState(false);
  const abbyyFlow = isAbbyyFlow(documentUpload?.ATD);
  const navigate = useNavigate();
  const mounted = useMounted();

  const [allowDesktopMode, setAllowDesktopMode] = useState(false);

  useEffect(() => {
    async function checkDesktopMode() {
      const allowed = await isAidUploadDocumentDesktopModeAllowed(
        applicationType
      );
      if (mounted) setAllowDesktopMode(allowed);
    }

    checkDesktopMode();
  }, [applicationType]);

  // Shared guard: on non-mobile devices, MDL-mobile flows can only proceed
  // via the desktop-mode allowance.
  const isDesktopUploadRestricted = () =>
    isMDLMobile(applicationType) && !isMobile && !allowDesktopMode;

  const showUploadPhoto = () => {
    if (isDesktopUploadRestricted()) {
      return false;
    }
    const isPendingUpload =
      documentUpload.statusId === DOC_UPLOAD_STATUS_IDS.PENDING;
    return (
      !isMobile &&
      !allowDesktopMode &&
      (isPendingUpload || documentErrorStatus === ABBYY_ERROR_STOP)
    );
  };

  const showTakePhoto = () => {
    if (isDesktopUploadRestricted()) {
      return false;
    }

    if (documentUpload.statusId === DOC_UPLOAD_STATUS_IDS.VERIFYING) {
      return false;
    }

    const isPendingUpload =
      documentUpload.statusId === DOC_UPLOAD_STATUS_IDS.PENDING;
    const isIdentityCategoryRetry =
      isIdentityCaptureCategory(documentUpload.categoryId) &&
      (isRetryStatus(documentUpload?.ATD) ||
        (isPendingConfirmationStatus(documentUpload?.ATD) && !confirmFields));
    const isMtfMtsRetry =
      documentUpload.categoryId === MTF_MTS_CATEGORY_ID &&
      (isRetryStatus(documentUpload?.MTF) ||
        isRetryStatus(documentUpload?.MTS));

    return (
      isPendingUpload ||
      isIdentityCategoryRetry ||
      isMtfMtsRetry ||
      documentErrorStatus === ABBYY_ERROR_STOP
    );
  };

  const isPassportOrPassportCard = (catDocId) =>
    isDocument(Documents.PASSPORT, documentTypes[catDocId]) ||
    isDocument(Documents.PASSPORT_CARD, documentTypes[catDocId]);

  const isIdentityFormDocument = (catDocId) =>
    isDocument(Documents.BIRTH_CERTIFICATE, documentTypes[catDocId]) ||
    isPassportOrPassportCard(catDocId);

  const handlePendingConfirmation = (documentDetails) => {
    if (isPassportOrPassportCard(documentUpload.catDocId)) {
      setPassportFormData({
        ...passportFormData,
        Passport_Number: documentDetails?.documentNumber,
        Passport_Expiration_Date: documentDetails?.documentExpirationDate,
      });
    }
    setShowIdentityForm(true);
  };

  const handleSuccessRedirect = () => {
    const url = generateUrl(application, documentUploads, applicationType, {
      successStatus: true,
    });
    navigate(url, { state: { successStatus: true } });
  };

  const handleAuthenticateDocumentResponse = ({ data }) => {
    transactionId = data.transactionId;
    setExtractedDetails(data.documentDetails);

    if (isPendingConfirmationStatus(data.decisionCode)) {
      handlePendingConfirmation(data.documentDetails);
      return;
    }

    const isEligibleForSuccessRedirect =
      isSuccessStatus(data.decisionCode) &&
      successRedirectApplicationTypes.includes(applicationType);

    if (isEligibleForSuccessRedirect) {
      handleSuccessRedirect();
    }
  };

  // Post authenticateDocument
  const postAuthenticateDocument = async () => {
    setExtractInProgress(true);
    const response = await authenticateDocument(
      documentUpload.id,
      applicationType
    );
    handleAuthenticateDocumentResponse(response);
    await fetchDocumentUploads();
    setProcessingPhotos(PROCESSING_PHOTOS_IDLE);
    setTakingPhotoDocumentId(null);
    setExtractInProgress(false);
  };

  const showChangeImage = () => {
    if (isDesktopUploadRestricted()) {
      return false;
    }
    return !inProgressOrFinalStatuses.includes(documentUpload.statusId);
  };

  const showActiveCheck = activeCheckStatuses.includes(documentUpload.statusId);

  const showSuccessCheck =
    DOC_UPLOAD_STATUS_IDS.VERIFIED === documentUpload.statusId;
  const isSelectedDocumentID = documentUpload.id === takingPhotoDocumentId;

  const getProcessingPhotosError = () => {
    if (aidCaptureErrorCode) {
      return t(
        `screen-aidCapture-errorCode${aidCaptureErrorCode}`,
        aidCaptureError[`errorCode${aidCaptureErrorCode}`]
      );
    }
    if (mutliPageError) {
      return t(
        "screens-Dashboard-mutliPageError",
        "Please provide required Front and Back photos of document."
      );
    }
    return t(
      "screens-Dashboard-processingPhotoError",
      "We encountered an error while processing your photos"
    );
  };

  const processingPhotosError = getProcessingPhotosError();

  const countErrorCodes = () => {
    const errorCodes = documentUpload.validationData?.errorCodes || [];
    // remove duplicate codes
    return errorCodes.filter(
      (code, index) => errorCodes.indexOf(code) === index
    ).length;
  };

  const uploadPhotos = async () => {
    const uploadSuccess = await postUploads(documentUpload);
    if (uploadSuccess) {
      setProcessingPhotos(PROCESSING_PHOTOS_SUCCESS);
    } else {
      setProcessingPhotos(PROCESSING_PHOTOS_ERROR);
    }
  };

  useEffect(() => {
    if (isLoadedOnce || (!isMobile && !allowDesktopMode) || !mounted) {
      return;
    }

    isLoadedOnce = true;

    initializeAID();
  }, [isMobile, mounted]);

  useEffect(() => {
    if (
      processingPhotos === PROCESSING_PHOTOS_SUCCESS &&
      isIdentityCaptureCategory(documentUpload.categoryId)
    ) {
      if (
        !isAbbyyFlow(documentUpload.ATD) &&
        !isSuccessStatus(documentUpload.ATD)
      ) {
        postAuthenticateDocument(documentUpload, applicationType);
        fetchDocumentUploads();
      }
    }
    if (processingPhotos === PROCESSING_PHOTOS_PROCESSING && abbyyFlow) {
      uploadPhotos();
    }
  }, [processingPhotos]);

  useEffect(() => {
    if (isIdentityCaptureCategory(documentUpload.categoryId)) {
      if (
        isPendingStatus(documentUpload.ATD) &&
        Object.keys(extractedDetails).length !== 0
      ) {
        // setShowConfirmFields(true);
      } else if (isRetryStatus(documentUpload.ATD)) {
        setShowIdentityForm(false);
      } else if (isFailureStatus(documentUpload.ATD)) {
        // setAidFailure(true);
      }
    }
    fetchDocumentUploads();
  }, [documentUpload.ATD]);

  const isAidEnabledDocument = () =>
    aidEnabledCatDocIds.includes(documentUpload.catDocId);

  const handleIdentityCaptureSetup = () => {
    const { catDocId } = documentUpload;

    if (isIdentityFormDocument(catDocId)) {
      setVerificationRequired("required");
    }

    if (isAIDCapture(documentUpload.ATD) && isAidEnabledDocument()) {
      setRequiredPhotos("required");
      return;
    }

    if (
      abbyyFlow &&
      isAidEnabledDocument() &&
      isPassportOrPassportCard(catDocId)
    ) {
      setRequiredPhotos("required");
    }
  };

  const onTakePhotoClick = () => {
    setTakePhotoClicked(true);
    setTakingPhotoDocumentId(documentUpload.id);
    resetUseABBYState();
    resetFormData();

    if (!isIdentityCaptureCategory(documentUpload.categoryId)) {
      return;
    }

    handleIdentityCaptureSetup();
  };

  const processingErrorModalClose = () => {
    if (mutliPageError) {
      if (
        isDocument(
          Documents.PASSPORT_CARD,
          documentTypes[documentUpload.catDocId]
        )
      ) {
        resetFormData();
      }
      setRequiredPhotos("required");
      setMultipageError(false);
    } else if (aidCaptureErrorCode) {
      setAidCaptureErrorCode(null);
    }
    setProcessingPhotos(PROCESSING_PHOTOS_IDLE);
    setTakingPhotoDocumentId(null);
  };

  const submitAIDCapture = () => {
    // setCloseModal(true)
    setProcessingPhotos(PROCESSING_PHOTOS_PROCESSING);
    setVerificationRequired("submitPhotos");
    uploadPhotos();
  };

  const isRequiredPhotosResolved = [null, "accepted"].includes(requiredPhotos);

  const shouldShowMobileVerificationModal =
    (isMobile || allowDesktopMode) &&
    ((verificationRequired === "required" &&
      isRequiredPhotosResolved &&
      takePhotoClicked &&
      isSelectedDocumentID) ||
      showIdentityForm);

  // dont allow abbyy if allowAbbyy is false
  const isAbbyyCaptureAllowed =
    isAbbyyFlow(documentUpload.ATD) && documentUpload.allowAbbyy !== false;

  const shouldShowRequiredPhotos =
    (isMobile || allowDesktopMode) &&
    requiredPhotos === "required" &&
    takePhotoClicked &&
    isSelectedDocumentID &&
    isAbbyyCaptureAllowed;

  return (
    <>
      <DocumentUpload
        legend={translatedDocumentName || documentUpload.name}
        containerClass={containerClass}
        showSelectDifferentDocument={
          showSelectDifferentDocument && !documentUpload.parentId
        }
        showUploadPhoto={showUploadPhoto()}
        showTakePhoto={showTakePhoto()}
        uploadImageLabel={t("screens-Dashboard-uploadPhoto", "Upload Photo")}
        takePhotoLabel={t("screens-Dashboard-takePhotoLabel", "Take Photo")}
        selectDifferentDocumentLabel={
          selectDifferentDocumentLabel ||
          t("screens-Dashboard-changeLabel", "Select a Different Document")
        }
        orLabel={t("screens-Dashboard-orLabel", "or")}
        onUploadClick={() => {
          resetFormData();
          setUploadingDocumentId(documentUpload.id);
        }}
        onTakePhotoClick={onTakePhotoClick}
        onSelectDifferentDocument={() => {
          if (onSelectDifferentDocument) {
            onSelectDifferentDocument();
          } else {
            setSelectDifferentDocumentId(documentUpload.id);
          }
        }}
        errorStatus={documentErrorStatus}
        errorTitle={
          documentErrorStatus === ABBYY_ERROR_STOP ? (
            <Trans
              i18nKey="screens-Dashboard-abbyyErrorTitle"
              defaults="There was an error in your submission. Please correct the following {{items}} or select a different document."
              values={{
                items: countErrorCodes() > 1 ? "items" : "item",
              }}
            />
          ) : (
            ""
          )
        }
        errorExplanation={<ErrorExplanation documentUpload={documentUpload} />}
        showChangeImage={showChangeImage()}
        onChangeImage={() => {
          setUploadingDocumentId(documentUpload.id);
        }}
        showActiveCheck={showActiveCheck}
        showSuccessCheck={showSuccessCheck}
      />
      {documentUpload.id === uploadingDocumentId && (
        <Upload
          onClose={() => {
            setUploadingDocumentId(null);
          }}
          documentUpload={documentUpload}
          modalTitle={`${t("shared.uploadLabel", "Upload")} ${translatedDocumentName || documentUpload.name
            }`}
          uploadDocumentFiles={uploadDocumentFiles}
        />
      )}
      {isSelectedDocumentID && !isMobile && !allowDesktopMode && (
        <TakePhotoButton documentUpload={documentUpload} />
      )}
      {isSelectedDocumentID &&
        requiredPhotos === "required" &&
        processingPhotos === PROCESSING_PHOTOS_IDLE &&
        (isMobile || allowDesktopMode) &&
        // dont allow abbyy if allowAbbyy is false
        (isAIDCapture(documentUpload.ATD) ||
          documentUpload.allowAbbyy === false) && (
          <AIDDocumentCapture
            documentName={documentUpload.name}
            applicationType={applicationType}
            onClose={() => {
              setRequiredPhotos("canceled");
              setTakingPhotoDocumentId(null);
            }}
            onCapture={() => {
              setVerificationRequired("accepted");
              setIsCapturing(CAPTURING_PHOTOS_IDLE);
            }}
            onConfirm={submitAIDCapture}
            requiredPages={documentUpload.requiredPages}
            document={documentUpload}
          />
        )}
      {shouldShowRequiredPhotos && (
        <RequiredPhotos
          documentName={documentUpload.name}
          onClose={() => {
            setRequiredPhotos("canceled");
            setTakingPhotoDocumentId(null);
            setTakePhotoClicked(false);
          }}
          onCapture={() => {
            if (isPassportOrPassportCard(documentUpload.catDocId)) {
              setShowIdentityForm(true);
            } else {
              setVerificationRequired("accepted");
            }
            setRequiredPhotos("accepted");
          }}
          onConfirm={() => {
            setVerificationRequired("submitPhotos");
          }}
          requiredPages={documentUpload.requiredPages}
          document={documentUpload}
        />
      )}
      {shouldShowMobileVerificationModal && (
        <MobileVerificationModal
          t={t}
          documentUpload={documentUpload}
          setVerificationRequired={setVerificationRequired}
          setShowIdentityForm={setShowIdentityForm}
          extractedDetails={extractedDetails}
          setConfirmFields={setConfirmFields}
          extractInProgress={extractInProgress}
          setExtractInProgress={setExtractInProgress}
          setRequiredPhotos={setRequiredPhotos}
          applicationType={applicationType}
          onClose={() => {
            if (documentUpload?.requiredPages) {
              setRequiredPhotos("required");
            }
            setTakingPhotoDocumentId(null);
            setShowIdentityForm(false);
            setTakePhotoClicked(false);
            // setIsCapturing(CAPTURING_PHOTOS_IDLE);
          }}
        />
      )}
      {((isSelectedDocumentID &&
        [PROCESSING_PHOTOS_UPLOADING, PROCESSING_PHOTOS_PROCESSING].includes(
          processingPhotos
        )) ||
        extractInProgress) &&
        (isMobile || allowDesktopMode) && <ProcessingPhotosModal t={t} />}
      {isSelectedDocumentID &&
        processingPhotos === PROCESSING_PHOTOS_SUCCESS &&
        (isMobile || allowDesktopMode) &&
        abbyyFlow && (
          <ProcessingPhotosSuccessModal
            onClose={() => {
              setProcessingPhotos(PROCESSING_PHOTOS_IDLE);
              setTakingPhotoDocumentId(null);
              fetchDocumentUploads();
            }}
            t={t}
          />
        )}
      {((isSelectedDocumentID &&
        processingPhotos === PROCESSING_PHOTOS_ERROR) ||
        mutliPageError ||
        aidCaptureErrorCode) &&
        (isMobile || allowDesktopMode) && (
          <ProcessingPhotosErrorModal
            onClose={processingErrorModalClose}
            error={processingPhotosError}
            t={t}
          />
        )}
      {selectDifferentDocumentId === documentUpload.id && onCancelDocument && (
        <SelectDifferentDocument
          onConfirm={onCancelDocument}
          onClose={() => {
            setSelectDifferentDocumentId(null);
            setTakingPhotoDocumentId(null);
          }}
        />
      )}
    </>
  );
}

export { transactionId };
